import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import login_logo from "../../assets/images/login_logo.png";
import { changePassword, logoutUser } from "../../store/slices/authSlice";
import { useAppSelector } from "../../hooks/useAppSelector";
import StatusPopup from "../../components/popups/StatusPopup";
import eye_icon from "../../assets/images/eye_icon.svg";
import eye_slash_icon from "../../assets/images/eye_slash_icon.svg";
import { encryptPassword } from "../../utils/cryptoUtils";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useAppSelector((s) => s.user);
  const { loading, error, success } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const [formErrors, setFormErrors] = useState({});

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.oldPassword) errors.oldPassword = 'Old password is required';
    if (!formData.newPassword) errors.newPassword = 'New password is required';
    if (!formData.confirmPassword) errors.confirmPassword = 'Confirm password is required';
    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const encryptedOldPassword = await encryptPassword(formData.oldPassword);
    const encryptedNewPassword = await encryptPassword(formData.newPassword);

    dispatch(changePassword({
      email: user.email,
      oldPassword: encryptedOldPassword,
      newPassword: encryptedNewPassword
    }));
  };

  useEffect(() => {
    if (success != null) {
      // Show success popup
      setShowSuccessPopup(true);
    }
  }, [success]);

  return (
    <div className="login py_14">
      <div className="container-fluid h-100">
        <div className="row h-100 justify-content-center">
          <div className="col-md-6">
            <div className="login_form h-100 d-flex flex-column align-item-center justify-content-center px_50">
              <img src={login_logo} className="w-auto mw-auto mb_60" alt="logo" />

              <h4 className="mb_4">Change Password</h4>
              <p>Please provide your credentials to reset your password.</p>

              <div className="common_input_forms mt_0">
                <form onSubmit={handleSubmit}>

                  <div className="form_group_wrapper">
                    <div className="form_group">
                      <label>Old Password</label>
                      <input
                        type={showOldPassword ? "text" : "password"}
                        name="oldPassword"
                        className="form-control"
                        placeholder="Enter old password"
                        value={formData.oldPassword}
                        onChange={handleInputChange}
                      />
                      <img
                        src={showOldPassword ? eye_slash_icon : eye_icon}
                        alt="Toggle password visibility"
                        className="eye-icon"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                      />
                    </div>
                    {formErrors.oldPassword && <div className="text-danger mt-2">{formErrors.oldPassword}</div>}
                  </div>

                  <div className="form_group_wrapper">
                    <div className="form_group">
                      <label>New Password</label>
                      <input
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        className="form-control"
                        placeholder="Enter new password"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                      />
                      <img
                        src={showNewPassword ? eye_slash_icon : eye_icon}
                        alt="Toggle password visibility"
                        className="eye-icon"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      />
                    </div>
                    {formErrors.newPassword && <div className="text-danger mt-2">{formErrors.newPassword}</div>}
                  </div>

                  <div className="form_group_wrapper">
                    <div className="form_group">
                      <label>Confirm Password</label>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        className="form-control"
                        placeholder="Confirm new password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                      />
                      <img
                        src={showConfirmPassword ? eye_slash_icon : eye_icon}
                        alt="Toggle password visibility"
                        className="eye-icon"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                    </div>
                    {formErrors.confirmPassword && <div className="text-danger mt-2">{formErrors.confirmPassword}</div>}
                  </div>

                  {error && <div className="text-danger mt_20">{error}</div>}

                  <div className="d-flex mt_30">
                    <div className="w-50 ms-auto">
                      <button
                        type="submit"
                        className="btn btn_primary px_30 ms-auto"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Changing
                          </>
                        ) : (
                          "Change"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div >

      <StatusPopup
        show={showSuccessPopup}
        type="success"
        title="Password Changed Successfully"
        message="Your password has been updated. You will be logged out and redirected to the login page."
        handleClose={() => {
          setShowSuccessPopup(false);
          dispatch(logoutUser());
          navigate('/login');
        }}
        onConfirm={() => {
          setShowSuccessPopup(false);
          dispatch(logoutUser());
          navigate('/login');
        }}
      />
    </div >
  );
};

export default ChangePassword;
